/**
 * `UppKonvaShape` subclasses, hydration from `UppKonvaShapeSource`, and serialization to DTO rows.
 */

import { floorPlanFiniteNumber } from '../../stage/geometry/floor-plan-konva-sync';
import type { UppKonvaShapeSource } from '../upp-konva-source';
import type { UppKonvaShape } from '../upp-konva-shape';
import { newUppKonvaShapeId } from '../upp-konva-shape-id';
import type { UppKonvaShapeKind } from '../upp-konva-types';
import { UppKonvaBarShape } from './upp-konva-bar-shape';
import { UppKonvaBilliardShape } from './upp-konva-billiard-shape';
import { UppKonvaDoorShape } from './upp-konva-door-shape';
import { UppKonvaFootballShape } from './upp-konva-football-shape';
import { UppKonvaGenericShape } from './upp-konva-generic-shape';
import { UppKonvaSofaShape } from './upp-konva-sofa-shape';
import { UppKonvaStairsShape } from './upp-konva-stairs-shape';
import { UppKonvaTableShape } from './upp-konva-table-shape';
import { UppKonvaVertexShape } from './upp-konva-vertex-shape';
import { UppKonvaWindowShape } from './upp-konva-window-shape';

function _dataFromWire(data: unknown): Record<string, unknown> {
    return typeof data === 'object' && data !== null && !Array.isArray(data)
        ? { ...(data as Record<string, unknown>) }
        : {};
}

function _defaultData(kind: UppKonvaShapeKind): Record<string, unknown> {
    switch (kind) {
        case 'VERTEX':
            return { sort: 0, closed: false, poligon: '' };
        case 'TABLE':
            return { shape: 'SQUARE', chairs: 0, diners: 0 };
        case 'BAR':
            return { shape: 'LINEAR' };
        case 'DOOR':
            return { shape: 'TOLEFT' };
        case 'WINDOW':
            return { shape: 'SINGLE' };
        case 'STAIRS':
            return { shape: 'SIMPLE' };
        case 'BILLIARD':
        case 'FOOTBALL':
            return {};
        case 'SOFA':
            return { shape: 'LINEAR' };
        case 'GENERIC':
            return { shape: 'SQUARE' };
        default: {
            const _e: never = kind;
            return _e;
        }
    }
}

function _commonFromSource(s: UppKonvaShapeSource): {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    label: string;
    sublabel: string;
    zIndex?: number;
    mode?: string;
    custom?: unknown;
} {
    const rawId = typeof s.id === 'string' ? s.id.trim() : '';
    return {
        id: rawId !== '' ? rawId : newUppKonvaShapeId(),
        x: floorPlanFiniteNumber(s.x),
        y: floorPlanFiniteNumber(s.y),
        width: floorPlanFiniteNumber(s.width),
        height: floorPlanFiniteNumber(s.height),
        rotation: floorPlanFiniteNumber(s.rotation),
        label: s.label ?? '',
        sublabel: s.sublabel ?? '',
        ...(s.zIndex !== undefined ? { zIndex: s.zIndex } : {}),
        ...(s.mode !== undefined ? { mode: s.mode } : {}),
        ...(s.custom !== undefined ? { custom: s.custom } : {}),
    };
}

function _vertexPoligonWire(merged: Record<string, unknown>): string {
    const a = typeof merged['poligon'] === 'string' ? merged['poligon'].trim() : '';
    const b = typeof merged['polygon'] === 'string' ? (merged['polygon'] as string).trim() : '';
    return a.length > 0 ? a : b;
}

function _kindFromSource(s: UppKonvaShapeSource): UppKonvaShapeKind | null {
    const kind = String(s.kind ?? '')
        .trim()
        .toUpperCase();
    switch (kind) {
        case 'VERTEX':
        case 'TABLE':
        case 'BAR':
        case 'DOOR':
        case 'WINDOW':
        case 'STAIRS':
        case 'BILLIARD':
        case 'SOFA':
        case 'FOOTBALL':
        case 'GENERIC':
            return kind;
        default:
            return null;
    }
}

/**
 * Builds a live shape from a document row (common geometry and per-kind `data`).
 *
 * English comment normalized.
 * English comment normalized.
 *
 * @param s - Wire row (`UppKonvaShapeSource`).
 * English comment normalized.
 */
export function uppKonvaShapeFromSource(s: UppKonvaShapeSource): UppKonvaShape | null {
    const common = _commonFromSource(s);
    const wireData = _dataFromWire(s.data);
    const attachWireData = (shape: UppKonvaShape): UppKonvaShape => {
        shape.data = wireData;
        return shape;
    };
    const kind = _kindFromSource(s);
    if (!kind) {
        return null;
    }
    switch (kind) {
        case 'VERTEX': {
            const merged = { ..._defaultData('VERTEX'), ...wireData };
            const sort =
                typeof merged['sort'] === 'number' && Number.isFinite(merged['sort']) ? (merged['sort'] as number) : 0;
            return attachWireData(
                new UppKonvaVertexShape({
                    ...common,
                    sort,
                    closed: merged['closed'] === true,
                    poligon: _vertexPoligonWire(merged),
                }),
            );
        }
        case 'TABLE': {
            const merged = { ..._defaultData('TABLE'), ...wireData };
            const chairs =
                typeof merged['chairs'] === 'number' && Number.isFinite(merged['chairs']) ? (merged['chairs'] as number) : 0;
            return attachWireData(
                new UppKonvaTableShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'SQUARE'),
                    chairs,
                }),
            );
        }
        case 'BAR': {
            const merged = { ..._defaultData('BAR'), ...wireData };
            const stoolLeft = merged['stoolLeft'];
            const stoolRight = merged['stoolRight'];
            const stoolFront = merged['stoolFront'];
            return attachWireData(
                new UppKonvaBarShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'LINEAR'),
                    ...(typeof stoolLeft === 'number' && Number.isFinite(stoolLeft) ? { stoolLeft } : {}),
                    ...(typeof stoolRight === 'number' && Number.isFinite(stoolRight) ? { stoolRight } : {}),
                    ...(typeof stoolFront === 'number' && Number.isFinite(stoolFront) ? { stoolFront } : {}),
                }),
            );
        }
        case 'DOOR': {
            const merged = { ..._defaultData('DOOR'), ...wireData };
            return attachWireData(
                new UppKonvaDoorShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'TOLEFT'),
                }),
            );
        }
        case 'WINDOW': {
            const merged = { ..._defaultData('WINDOW'), ...wireData };
            return attachWireData(
                new UppKonvaWindowShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'SINGLE'),
                }),
            );
        }
        case 'STAIRS': {
            const merged = { ..._defaultData('STAIRS'), ...wireData };
            return attachWireData(
                new UppKonvaStairsShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'SIMPLE'),
                }),
            );
        }
        case 'BILLIARD':
            return attachWireData(new UppKonvaBilliardShape({ ...common }));
        case 'SOFA': {
            const merged = { ..._defaultData('SOFA'), ...wireData };
            return attachWireData(
                new UppKonvaSofaShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'LINEAR'),
                }),
            );
        }
        case 'FOOTBALL':
            return attachWireData(new UppKonvaFootballShape({ ...common }));
        case 'GENERIC': {
            const merged = { ..._defaultData('GENERIC'), ...wireData };
            return attachWireData(
                new UppKonvaGenericShape({
                    ...common,
                    shape: String(merged['shape'] ?? 'SQUARE'),
                }),
            );
        }
    }
}

/**
 * Applies a wire row over an existing live shape preserving object identity (and observers).
 *
 * @param shape - Live shape instance to mutate.
 * @param s - Incoming wire row.
 * @returns `true` when kind and id are compatible and patch was applied.
 */
export function uppKonvaShapePatchFromSource(shape: UppKonvaShape, s: UppKonvaShapeSource): boolean {
    const kind = _kindFromSource(s);
    if (!kind || kind !== shape.kind) {
        return false;
    }
    const sourceId = typeof s.id === 'string' ? s.id.trim() : '';
    if (sourceId !== '' && sourceId !== shape.id.trim()) {
        return false;
    }
    const nx = s.x ?? 0;
    const ny = s.y ?? 0;
    const nw = s.width ?? 0;
    const nh = s.height ?? 0;
    const nr = s.rotation ?? 0;
    const nlabel = s.label ?? '';
    const nsublabel = s.sublabel ?? '';
    const nextData = _dataFromWire(s.data);
    const dataChanged = JSON.stringify(shape.data) !== JSON.stringify(nextData);
    const changed =
        nx !== shape.x ||
        ny !== shape.y ||
        nw !== shape.width ||
        nh !== shape.height ||
        nr !== shape.rotation ||
        nlabel !== shape.label ||
        nsublabel !== shape.sublabel ||
        s.zIndex !== shape.zIndex ||
        s.mode !== shape.mode ||
        s.custom !== shape.custom ||
        dataChanged;

    shape.x = nx;
    shape.y = ny;
    shape.width = nw;
    shape.height = nh;
    shape.rotation = nr;
    shape.label = nlabel;
    shape.sublabel = nsublabel;
    shape.zIndex = s.zIndex;
    shape.mode = s.mode;
    shape.custom = s.custom;
    shape.data = nextData;

    if (changed) {
        shape.notifyUpdated();
    }
    return true;
}

/**
 * Serializes a live shape to the document contract (merges per-kind defaults with live `data`).
 *
 * @param s - Live shape instance.
 * @returns `UppKonvaShapeSource` row.
 */
export function uppKonvaShapeToSource(s: UppKonvaShape): UppKonvaShapeSource {
    const base: UppKonvaShapeSource = {
        id: s.id,
        kind: s.kind,
        x: s.x,
        y: s.y,
        width: s.width,
        height: s.height,
        rotation: s.rotation,
        ...(s.label !== '' ? { label: s.label } : {}),
        ...(s.sublabel !== '' ? { sublabel: s.sublabel } : {}),
        ...(s.zIndex !== undefined ? { zIndex: s.zIndex } : {}),
        ...(s.mode !== undefined ? { mode: s.mode } : {}),
        ...(s.custom !== undefined ? { custom: s.custom } : {}),
    };
    const merged = { ..._defaultData(s.kind), ...s.data };
    if (Object.keys(merged).length > 0) {
        return { ...base, data: merged };
    }
    return base;
}

export { UppKonvaBarShape } from './upp-konva-bar-shape';
export { UppKonvaBilliardShape } from './upp-konva-billiard-shape';
export { UppKonvaDoorShape } from './upp-konva-door-shape';
export { UppKonvaFootballShape } from './upp-konva-football-shape';
export { UppKonvaGenericShape } from './upp-konva-generic-shape';
export { UppKonvaSofaShape } from './upp-konva-sofa-shape';
export { UppKonvaStairsShape } from './upp-konva-stairs-shape';
export { UppKonvaTableShape } from './upp-konva-table-shape';
export { UppKonvaVertexShape } from './upp-konva-vertex-shape';
export { UppKonvaWindowShape } from './upp-konva-window-shape';

export type {
    UppKonvaBarShapeOptions,
    UppKonvaBilliardShapeOptions,
    UppKonvaDoorShapeOptions,
    UppKonvaFootballShapeOptions,
    UppKonvaGenericShapeOptions,
    UppKonvaShapeBaseInit,
    UppKonvaShapeCommonOptions,
    UppKonvaShapeWireMeta,
    UppKonvaSofaShapeOptions,
    UppKonvaStairsShapeOptions,
    UppKonvaTableShapeOptions,
    UppKonvaVertexShapeOptions,
    UppKonvaWindowShapeOptions,
} from '../upp-konva-options';
